import React, { useState } from "react";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import StarIcon from "@material-ui/icons/Star";
import ReceiptIcon from "@material-ui/icons/Receipt";
import Button from "designV2/components/Button";
import StatusBadge from "designV2/components/StatusBadge";

const REPLY_TO_REVIEW = gql`
  mutation setShopAdminReplyOnComment($comment: ID!, $text: String!) {
    setShopAdminReplyOnComment(input: { comment: $comment, text: $text }) {
      _id
      userComment
      createdAt
    }
  }
`;

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

/**
 * Reply - the body of a review dialog: the rating breakdown, the
 * customer's comment, and either the shop's one-time reply or a
 * composer to write it. Rendered inside a DialogShell by Reviews.js,
 * which owns the title/subtitle/close button.
 *
 * A review only ever gets a single reply from the shop — unlike a
 * conversation there's no back-and-forth — so once `reply` is set
 * (from the record itself or the mutation response) the composer is
 * replaced with the posted reply instead of the dialog closing, which
 * is what the pre-redesign version did.
 */
const Reply = ({ review, onSent, onViewOrder }) => {
  const [text, setText] = useState("");
  const [reply, setReply] = useState(review.shopAdminReply && review.shopAdminReply.comment);
  const [error, setError] = useState("");

  const [sendReply, { loading: sending }] = useMutation(REPLY_TO_REVIEW);

  const onSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    setError("");
    sendReply({ variables: { comment: review._id, text: trimmed } })
      .then((res) => {
        setReply(res.data.setShopAdminReplyOnComment);
        setText("");
        if (onSent) onSent();
      })
      .catch((err) => setError(readableError(err)));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" || e.charCode === 13) {
      e.preventDefault();
      onSubmit();
    }
  };

  const rates = review.readyComments || [];

  return (
    <div className="reviewThread">
      {rates.length > 0 && (
        <div className="reviewRatingBreakdown">
          {rates.map((rc, index) => (
            <span key={rc.readyComment ? rc.readyComment._id : index} className="reviewRatingChip">
              {rc.readyComment ? rc.readyComment.type : "Rating"}
              <StarIcon fontSize="small" /> {rc.rate}
            </span>
          ))}
        </div>
      )}

      {review.order && (
        <button type="button" className="reviewViewOrderLink" onClick={() => onViewOrder(review.order)}>
          <ReceiptIcon fontSize="small" /> View linked order
        </button>
      )}

      <div className="reviewChat">
        <div className="reviewBubbleRow isOther">
          <div className="reviewSenderLabel">{(review.user && review.user.fullName) || "Customer"}</div>
          <div className="reviewBubble">
            {review.userComment || "No written comment"}
            <span className="reviewBubbleTime">{moment(review.createdAt).format("MMM D, hh:mm A")}</span>
          </div>
        </div>

        {reply && (
          <div className="reviewBubbleRow isAdmin">
            <div className="reviewSenderLabel">Your reply</div>
            <div className="reviewBubble">
              {reply.userComment}
              <span className="reviewBubbleTime">{moment(reply.createdAt).format("MMM D, hh:mm A")}</span>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="dsFormMessage error" role="alert">
          {error}
        </div>
      )}

      {!reply ? (
        <div className="reviewComposer">
          <input
            type="text"
            className="dsInput"
            value={text}
            placeholder="Write a public reply..."
            onChange={(e) => setText(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button variant="primary" onClick={onSubmit} disabled={!text.trim()} loading={sending}>
            Send
          </Button>
        </div>
      ) : (
        <p className="reviewPanelHint">
          <StatusBadge status="success" label="Replied" dot={false} /> Reviews only get one reply from the shop.
        </p>
      )}
    </div>
  );
};

export default Reply;
